/**
 * Where Stripe returns the customer after Checkout.
 *
 * ## Being landed on here does not mean the payment succeeded
 *
 * Stripe redirects on completion of the *page*, not on settlement. The webhook
 * is what confirms money moved, and it can arrive a second before this render
 * or a few seconds after. So this screen deliberately does **not** say "you're
 * on Premium" — it polls `/auth/me` until the plan actually changes, and says
 * something honest while it waits.
 *
 * Claiming success from the redirect alone is the classic version of this bug:
 * it reads correctly in every test, and then tells a clinic whose card was
 * declined at the final step that they have upgraded.
 */
import { SUPPORT_EMAIL } from '@inventory-tracker/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getMe, type Me } from '../lib/api.js';
import { c, primaryButton, sans } from '../theme.js';

/** How long to wait for the webhook before offering a way out. */
const GIVE_UP_AFTER_MS = 20_000;
const POLL_EVERY_MS = 1_500;

interface Props {
  /** The plan the account was on before checkout, to detect the change. */
  previous: Me | null;
  onContinue: () => void;
}

export function BillingDoneScreen({ previous, onContinue }: Props) {
  const [me, setMe] = useState<Me | null>(previous);
  const [waited, setWaited] = useState(0);
  const timer = useRef<number | null>(null);

  const changed =
    me !== null &&
    previous !== null &&
    (me.subscription.plan !== previous.subscription.plan ||
      me.subscription.status !== previous.subscription.status ||
      me.subscription.cycle !== previous.subscription.cycle);

  const poll = useCallback(async () => {
    const next = await getMe();
    if (next) setMe(next);
  }, []);

  useEffect(() => {
    if (changed) return;
    if (waited >= GIVE_UP_AFTER_MS) return;
    timer.current = window.setTimeout(() => {
      setWaited((n) => n + POLL_EVERY_MS);
      void poll().catch(() => undefined);
    }, POLL_EVERY_MS);
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [changed, waited, poll]);

  const timedOut = !changed && waited >= GIVE_UP_AFTER_MS;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: c.appBg,
        color: c.ink,
        fontFamily: sans,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 460, textAlign: 'center' }}>
        <h1 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 30, margin: '0 0 12px' }}>
          {changed ? 'You’re all set' : timedOut ? 'Still processing' : 'Confirming your payment'}
        </h1>

        <p style={{ fontSize: 14.5, color: c.muted, lineHeight: 1.75, margin: '0 0 22px' }}>
          {changed ? (
            <>
              Your clinic is on <strong>{me?.subscription.plan}</strong>, billed{' '}
              {me?.subscription.cycle}. A receipt is on its way from Stripe.
            </>
          ) : timedOut ? (
            <>
              Stripe has taken the payment but we have not had confirmation yet. This usually
              settles within a minute or two. Nothing is lost, and your plan will update on its
              own. If it still looks wrong tomorrow, email {SUPPORT_EMAIL} and quote your email address.
            </>
          ) : (
            <>
              Stripe confirms payments to us separately from sending you back here, so this can
              take a few seconds. Nothing else is needed from you.
            </>
          )}
        </p>

        <button
          type="button"
          onClick={onContinue}
          style={{ ...primaryButton, width: 'auto', padding: '0 26px', height: 46 }}
        >
          {changed ? 'Go to your stock' : 'Continue to the app'}
        </button>
      </div>
    </div>
  );
}
